import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * `SubmitShortcutHint` renders a styled keyboard shortcut hint (`Ctrl/⌘ + Enter`)
 * to inform users how to submit the question using a keyboard shortcut.
 *
 * ### Features
 * - Displays a localized instructional message
 * - Uses consistent styling for `kbd` elements based on the theme
 * - Helps reinforce accessibility and discoverability of keyboard shortcuts
 *
 * @component
 *
 * @example
 * function Footer() {
 *   return <SubmitShortcutHint />;
 * }
 *
 * @returns {JSX.Element} A caption element with keyboard shortcut styling and instructions.
 */
const SubmitShortcutHint = () => {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "common" });

    const Kbd = ({ children }: { children: string }) => (
        <kbd
            style={{
                padding: "2px 6px",
                backgroundColor: theme.palette.shortCutKeyBackground,
                color: theme.palette.disclaimerText,
                border: "1px solid",
                borderColor: theme.palette.shortCutKeyBackground,
                borderRadius: "4px",
                fontSize: "0.75rem",
                fontWeight: 500
            }}
        >
            {children}
        </kbd>
    );

    return (
        <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontSize: "0.75rem", padding: "4px 0px" }}>
            {t("sendShortcut.before")}
            <Kbd>Enter</Kbd>
            {t("sendShortcut.after")}
        </Typography>
    );
};

export default SubmitShortcutHint;
